import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Paper,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table,
    Checkbox,
} from 'rsuite';
import _ from 'lodash';
import { SplitButton } from '@commonClient2/components';
import { useAvailableHeight } from '@commonClient2/hooks';
import {
    UkgDateFormat, ManagerPage, ExportActions,
} from '@enums/index';
import { getProfileStatusLabel } from '@enums/Helpers/LabellingHelper';
import DateFormatService from '@services/DateFormatService';
import FilterSelectionBar from '@manager/BiddingList/FilterSelectionBar';
import AppBar from '@manager/AppBar';
import {
    loadAuditReportBids,
} from '@manager/store/appData';
import { downloadReportAsCsv } from '@store/auditReport';
import AuditReportNameCell from './AuditReportNameCell';
import AuditReportTooltipCell from './AuditReportTooltipCell';
import { searchableAuditBid, handleSort } from './AuditReport.service';
import './AuditReport.scss';

const rowKey = 'id';
/** @typedef {'asc'|'desc'} SortType */
/** @type {SortType} */
const defaultSortType = 'asc';

const { Column, HeaderCell, Cell } = Table;

function AuditReport() {
    const { t: tAudit } = useTranslation('shiftBidding', { keyPrefix: 'manager.auditReports' });
    const dispatch = useDispatch();

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData, pendingTasks } = useSelector((state) => state);
    const {
        pageName, auditBids,
    } = managerAppData;

    const allBids = auditBids;
    const [sortValues, setSortValues] = useState({ sortColumn: 'name', sortType: defaultSortType });
    /** @type {[string[], Function]} */
    const [checkedKeys, setCheckedKeys] = useState([]);
    /** @type {[string, Function]} */
    const [searchValue, setSearchValue] = useState('');
    /** @type {[number[], Function]} */
    const [statusFilterSelected, setStatusFilterSelected] = useState([]);
    /** @type {[ManagerAppData.IAuditableBid[], Function]} */
    // @ts-ignore
    const [filteredData, setFilteredData] = useState(allBids);
    const allStatus = [..._.unionWith(allBids, (a, b) => a.status === b.status).map((x) => x.status)];

    const ref = useRef(null);
    const height = useAvailableHeight(ref) || 0;

    function handleFilter() {
        // @ts-ignore
        let filteredBids = auditBids.sort((a, b) => b.statusDate - a.statusDate);
        if (!searchValue && !statusFilterSelected?.length) {
            setFilteredData(filteredBids);
        }

        // filter by search value
        if (searchValue) {
            filteredBids = filteredBids.filter((bid) => searchableAuditBid(bid).includes(searchValue.toLocaleLowerCase()));
        }
        // filter by status
        if (statusFilterSelected.length) {
            filteredBids = filteredBids.filter((bid) => statusFilterSelected.includes(bid.status));
        }
        setFilteredData(filteredBids);
    }
    /**
     * @param {string} id
     * @param {boolean} checked
     */
    const handleCheck = (id, checked) => {
        // TODO 2022-05-09 - future feature - allow multiple selection
        if (checked === false) {
            setCheckedKeys([]);
            return;
        }
        setCheckedKeys([id]);
    };

    /**
     * @param {moment.Moment|null} date
     * @returns {string}
     */
    useEffect(() => {
        dispatch(loadAuditReportBids());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        handleFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue, JSON.stringify(statusFilterSelected), JSON.stringify(auditBids)]);

    useEffect(() => {
        handleSort(filteredData, setFilteredData, sortValues.sortColumn, sortValues.sortType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(sortValues)]);

    return (
        <>
            <AppBar
                pageName={pageName}
                pageSelected={ManagerPage.Audit}
            />
            <div className="audit-report-full-page">
                <div
                    className="header-container"
                >
                    <SplitButton
                        options={[
                            { label: tAudit('actions.exportManager'), value: ExportActions.ExportManager, disabled: false },
                            { label: tAudit('actions.exportEmployee'), value: ExportActions.ExportEmployee, disabled: false },
                        ]}
                        color="primary"
                        handleClick={
                            /** @param {{label: string, value: number, disabled: boolean}} selectedOption */
                            (selectedOption) => dispatch(downloadReportAsCsv(checkedKeys, selectedOption.value))
                        }
                        disabled={!!pendingTasks || !checkedKeys.length}
                    />
                    <FilterSelectionBar
                        setStatusFilterSelected={setStatusFilterSelected}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        allStatus={allStatus}
                    />
                </div>
                <Paper
                    elevation={2}
                    className="bid-profile-table-container"
                    ref={ref}
                >
                    <Table
                        sortColumn={sortValues.sortColumn}
                        sortType={sortValues.sortType}
                        onSortColumn={(sortColumn, sortType) => setSortValues({ sortColumn, sortType })}
                        height={height}
                        loading={!!pendingTasks}
                        data={filteredData}
                        id="audit-bid-profile-table"
                        className="table-section-audit"
                        rowKey={rowKey}
                        shouldUpdateScroll={false}
                    >
                        <Column
                            width={50}
                            align="center"
                        >
                            <HeaderCell />
                            <Cell>
                                {/** @param {ManagerAppData.IAuditableBid} rowData */
                                    (rowData) => (
                                        <div style={{ lineHeight: '46px' }}>
                                            <Checkbox
                                                value={rowData.profileId}
                                                inline
                                                onChange={handleCheck}
                                                checked={checkedKeys.some((item) => item === rowData.profileId)}
                                            />
                                        </div>
                                    )
                                }
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                            key="name"
                        >
                            <HeaderCell>{tAudit('tableHeaders.name')}</HeaderCell>
                            {
                                // @ts-ignore
                                <AuditReportNameCell
                                    dataKey="name"
                                    iconTooltipLabel={tAudit('tooltip.rebidParent')}
                                />
                            }
                        </Column>
                        <Column
                            flexGrow={3}
                            sortable
                        >
                            <HeaderCell>{tAudit('tableHeaders.description')}</HeaderCell>
                            <AuditReportTooltipCell
                                dataKey="description"
                                getValue={(rowData) => rowData.description}
                            />
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{tAudit('tableHeaders.status')}</HeaderCell>
                            <Cell
                                dataKey="status"
                            >
                                {(/** @type {ManagerAppData.IAuditableBid} */ rowData) => getProfileStatusLabel(rowData.status)}
                            </Cell>
                        </Column>
                        <Column
                            flexGrow={2}
                            sortable
                        >
                            <HeaderCell>{tAudit('tableHeaders.statusDate')}</HeaderCell>
                            <Cell
                                dataKey="statusDate"
                            >
                                {(/** @type {ManagerAppData.IAuditableBid} */ rowData) => DateFormatService.formatDateOrEmpty(
                                    rowData.statusDate,
                                    UkgDateFormat.LongDateTime,
                                )}
                            </Cell>
                        </Column>
                    </Table>
                </Paper>
            </div>
        </>
    );
}
export default AuditReport;
